import {
    FaceTecSessionRequestProcessor,
    FaceTecSessionResult,
    type FaceTecSessionRequestProcessorCallback,
} from "src/assets/core-sdk/FaceTecSDK.js/FaceTecPublicApi";
import { SampleAppNetworkingRequest } from "./utilities/SampleAppNetworkingRequest";
import type { CaptureResponse } from "./utilities/SampleAppNetworkingRequest";
import { FaceTecSDKServiceV10 } from "./FaceTecSDKServiceV10";

// This class demonstrates the most important integration point in the FaceTec Device SDK  -- The Session Request Processor.
//
// The Session Request Processor:
// - Implements an onSessionRequest function.  This function is called by the FaceTec Device SDK.
// - The onSessionRequest function is always passed a sessionRequestBlob, and you always send this blob up to your webservice, and into FaceTec Server.
// - The Session Request Blob is an encrypted string that you get and send to FaceTec Server.
// - Upon receiving the Response Blob from FaceTec Server, you will always call the sessionRequestCallback.processResponse function.
// - The Session Response Blob is an encrypted string that you get from FaceTec Server and pass back into the Device SDK.
// - The Session Request Callback  This is called when you receive a response from the FaceTec Server, with the blob that you receive from FaceTec Server.
// - The Session Request Callback provides a updateProgress function, where you can pass in the upload progress, controlling the Upload Screen Progress Bar.
//
// Notes:
// - Adding additional logic to this code is not allowed.  Do not add any additional logic outside of what is demonstrated in this Sample.
// - Adding additional asynchronous calls to this code is not allowed.  Only make your own additional asynchronous calls once the FaceTec UI is closed.
// - Adding code that modifies any App UI (Yours or FaceTec's) is not allowed.  Only add code that modifies your own App UI once the FaceTec UI is closed.
export class SessionRequestProcessor implements FaceTecSessionRequestProcessor {
    // The onSessionRequest API is the core method called by the FaceTec SDK when a request needs to be processed by the FaceTec SDK.
    // Your code must retrieve the Session Request Blob and send to your FaceTec Server.
    // Your code must retrieve the Response Blob from FaceTec Server and call processResponse, passing in the Response Blob.

    // TODO: Important: Axon Code This must be restored in case of a update
    public axonFacetecSDKService: FaceTecSDKServiceV10;
    // A new processor is created for every capture session, including a user retry.
    private readonly trackedCaptureEvents = new Set<string>();
    constructor(axonFacetecSDKService: FaceTecSDKServiceV10) {
        this.axonFacetecSDKService = axonFacetecSDKService as FaceTecSDKServiceV10;
    }

    public trackCaptureJourney = (response: CaptureResponse): void => {
        const result = response.result;
        if (response.didError === true || result?.didError === true || response.success === false || result?.success === false) {
            return;
        }
        if (response.didError !== false && result?.didError !== false && response.success !== true && result?.success !== true) {
            return;
        }

        const type = response.type ?? result?.type;
        if (type === "NEW_ENROLLMENT") {
            this.trackCaptureEventOnce("sdk_face_capture_success");
            this.trackCaptureEventOnce("sdk_photo_id_capture_start");
        } else if (type === "MATCH_3D_2D_ID_SCAN") {
            this.trackCaptureEventOnce("sdk_photo_id_capture_success");
        }
    };

    private trackCaptureEventOnce = (page: string): void => {
        if (this.trackedCaptureEvents.has(page)) {
            return;
        }
        this.trackedCaptureEvents.add(page);
        this.axonFacetecSDKService.trackCaptureJourneyEvent(page);
    };

    public onSessionRequest = (
        sessionRequestBlob: string,
        sessionRequestCallback: FaceTecSessionRequestProcessorCallback,
    ): void => {
        // When you receive a Session Request Blob, call your webservice API that handles this object and passes it to FaceTec Server.
        // SampleAppNetworkingRequest is a demonstration class for making a networking call that passes the Session Request Blob, and handles the response.
        SampleAppNetworkingRequest.send(this, sessionRequestBlob, sessionRequestCallback);
    };

    // When the request blob has been received, send it back to the FaceTecSDK for continued processing
    public onResponseBlobReceived = (
        responseBlob: string,
        sessionRequestCallback: FaceTecSessionRequestProcessorCallback,
    ): void => {
        sessionRequestCallback.processResponse(responseBlob);
    };

    // When upload progress is received from your webservice, call updateProgress to update the Progress Bar state.
    // Please note that onUploadProgress is a convenience function set up on this class,
    // so that this function can be called asynchronously when your networking code receives an upload progress event.
    public onUploadProgress = (
        progress: number,
        sessionRequestCallback: FaceTecSessionRequestProcessorCallback,
    ): void => {
        sessionRequestCallback.updateProgress(progress);
    };

    // Calling abortOnCatastrophicError is not allowed except for catastrophic network failures.
    // Calling abortOnCatastrophicError to exit the FaceTec UI with custom logic is not allowed.
    public onCatastrophicNetworkError = (
        sessionRequestCallback: FaceTecSessionRequestProcessorCallback,
    ): void => {
        sessionRequestCallback.abortOnCatastrophicError();
    };

    // The onFaceTecExit API is the method called when the FaceTec SDK completes or cancels.
    // For demonstration purposes, we are handling next steps in the SampleAppController.
    public onFaceTecExit = (faceTecSessionResult: FaceTecSessionResult): void => {
        // TODO: Important: Axon Code This must be restored in case of a update
        this.axonFacetecSDKService.handleFaceTecExit(faceTecSessionResult);
    };
}
